import React from 'react';
import styled from 'styled-components';
import type { EventOrderFormField } from '../../hooks/useEvents';
import {
  buildFormResponsePayload,
  composeFullNameFromEditState,
  resolveOrderLevelEditableFields,
  resolvePerAttendeeEditableFields,
  isAttendeeSlotCheckedIn,
  parseFieldOptions,
  parseRegistrationEditState,
  MAX_REGISTRANT_EDIT_QUANTITY,
  type CustomFieldInputValue,
  type RegistrantEditAttendee,
  type RegistrantEditState,
  validateRegistrantEditState,
} from '../../utils/eventRegistrationFormFields';

export type RegistrantEditRegistration = {
  id: string;
  full_name: string;
  email: string;
  phone: string | null;
  notes: string | null;
  quantity: number;
  form_response: Record<string, unknown>;
};

export type RegistrantEditTicketRow = {
  registration_id: string;
  ticket_number: string | null;
  status: string | null;
  checked_in_at: string | null;
};

type Notice = {
  kind: 'success' | 'error' | 'info';
  message: string;
};

type EventRegistrantEditModalProps = {
  registration: RegistrantEditRegistration;
  formFields: EventOrderFormField[];
  registrationTickets: RegistrantEditTicketRow[];
  isSaving: boolean;
  onClose: () => void;
  onSave: (payload: {
    registrationId: string;
    fullName: string;
    email: string;
    phone: string;
    notes: string;
    quantity: number;
    formResponse: Record<string, unknown>;
  }) => Promise<void>;
};

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1300;
  display: grid;
  place-items: center;
  background: rgba(15, 23, 42, 0.45);
  padding: 1rem;

  @media (max-width: 640px) {
    padding: 0;
    place-items: end center;
  }
`;

const Modal = styled.div`
  width: min(760px, 100%);
  max-height: calc(100vh - 2rem);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background: var(--surface);
  border: 1px solid color-mix(in srgb, var(--outline) 78%, transparent);
  border-radius: 20px;
  box-shadow:
    0 0 0 1px color-mix(in srgb, white 6%, transparent),
    0 28px 56px rgba(15, 23, 42, 0.14),
    0 10px 24px rgba(15, 23, 42, 0.06);

  @media (max-width: 640px) {
    max-height: min(92dvh, calc(100vh - 1rem));
    border-radius: 20px 20px 0 0;
    width: 100%;
    max-width: 100%;
  }
`;

const ModalAccent = styled.div`
  height: 3px;
  width: 100%;
  flex-shrink: 0;
  background: linear-gradient(
    90deg,
    color-mix(in srgb, var(--primary) 82%, transparent) 0%,
    color-mix(in srgb, var(--primary) 42%, transparent) 48%,
    color-mix(in srgb, var(--primary) 10%, transparent) 100%
  );
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.85rem;
  flex-shrink: 0;
  padding: 1.1rem 1.35rem 1rem;
  border-bottom: 1px solid color-mix(in srgb, var(--outline) 52%, transparent);
  background: color-mix(in srgb, var(--surfaceVariant) 38%, var(--surface));
`;

const ModalTitle = styled.h2`
  margin: 0;
  font-size: 1.15rem;
  font-weight: 800;
  letter-spacing: -0.03em;
  color: var(--onSurface);
`;

const CloseButton = styled.button`
  border: 1px solid color-mix(in srgb, var(--outline) 85%, transparent);
  background: var(--surface);
  color: var(--onSurface);
  border-radius: 999px;
  padding: 0.45rem 0.85rem;
  font-size: 0.84rem;
  font-weight: 600;
  cursor: pointer;
`;

const ModalScrollBody = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 0.85rem 1.1rem 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.55rem;
`;

const Banner = styled.div<{ $kind: 'success' | 'error' | 'info' }>`
  padding: 0.6rem 0.75rem;
  border-radius: 10px;
  font-size: 0.84rem;
  line-height: 1.4;
  color: ${({ $kind }) => ($kind === 'error' ? 'var(--error)' : $kind === 'success' ? '#047857' : 'var(--onSurface)')};
  background: ${({ $kind }) =>
    $kind === 'error'
      ? 'color-mix(in srgb, var(--error) 12%, var(--surface))'
      : $kind === 'success'
        ? 'color-mix(in srgb, #047857 12%, var(--surface))'
        : 'color-mix(in srgb, var(--primary) 10%, var(--surface))'};
  border: 1px solid
    ${({ $kind }) =>
      $kind === 'error'
        ? 'color-mix(in srgb, var(--error) 28%, transparent)'
        : $kind === 'success'
          ? 'color-mix(in srgb, #047857 28%, transparent)'
          : 'color-mix(in srgb, var(--primary) 24%, transparent)'};
`;

// Use div, not section: global index.css targets section { padding: 90px 0 }.
const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
`;

const SectionPanel = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.45rem;
  padding: 0.7rem 0.8rem;
  border-radius: 12px;
  border: 1px solid color-mix(in srgb, var(--outline) 58%, transparent);
  background: color-mix(in srgb, var(--surfaceVariant) 22%, var(--surface));
`;

const SectionTitle = styled.h3`
  margin: 0;
  font-size: 0.64rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--onSurfaceVariant);
`;

const SectionHint = styled.p`
  margin: -0.1rem 0 0;
  font-size: 0.78rem;
  line-height: 1.35;
  color: var(--onSurfaceVariant);
`;

const fieldControlStyles = `
  width: 100%;
  box-sizing: border-box;
  border-radius: 10px;
  border: 1px solid color-mix(in srgb, var(--outline) 85%, transparent);
  padding: 0.5rem 0.7rem;
  background: var(--surface);
  color: var(--onSurface);
  font-size: 0.88rem;
  font-family: inherit;
  line-height: 1.35;
  box-shadow: inset 0 1px 2px rgba(15, 23, 42, 0.03);
  transition:
    border-color 0.15s ease,
    box-shadow 0.15s ease;

  &::placeholder {
    color: var(--onSurfaceVariant);
    opacity: 0.8;
  }

  &:focus {
    outline: none;
    border-color: color-mix(in srgb, var(--primary) 50%, var(--outline));
    box-shadow:
      inset 0 1px 2px rgba(15, 23, 42, 0.03),
      0 0 0 3px color-mix(in srgb, var(--primary) 18%, transparent);
  }
`;

const Field = styled.label`
  display: flex;
  flex-direction: column;
  gap: 0.28rem;
  font-size: 0.78rem;
  color: var(--onSurfaceVariant);
`;

const FieldLabel = styled.span`
  font-weight: 600;
  color: color-mix(in srgb, var(--onSurface) 82%, var(--onSurfaceVariant));
`;

const Input = styled.input`
  ${fieldControlStyles}
`;

const Textarea = styled.textarea`
  ${fieldControlStyles}
  min-height: 3.25rem;
  resize: vertical;
`;

const Select = styled.select`
  ${fieldControlStyles}
`;

const HelpText = styled.span`
  font-size: 0.74rem;
  line-height: 1.35;
  color: var(--onSurfaceVariant);
`;

const AttendeeStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.45rem;
`;

const AttendeeCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.45rem;
  padding: 0.65rem 0.75rem;
  border-radius: 11px;
  border: 1px solid color-mix(in srgb, var(--outline) 55%, transparent);
  background: var(--surface);
`;

const AttendeeHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
`;

const QuantityRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
`;

const QuantityControls = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
`;

const QuantityValue = styled.span`
  min-width: 1.5rem;
  text-align: center;
  font-size: 0.88rem;
  font-weight: 600;
  color: var(--onSurface);
`;

const AttendeeTitle = styled.strong`
  color: var(--onSurface);
  font-size: 0.84rem;
`;

const CheckedInBadge = styled.span`
  font-size: 0.72rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #047857;
`;

const ToolbarActionRow = styled.div`
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
  margin-top: 0.15rem;
  padding-top: 0.65rem;
  border-top: 1px solid color-mix(in srgb, var(--outline) 52%, transparent);
`;

const ActionButton = styled.button`
  border: 1px solid color-mix(in srgb, var(--outline) 85%, transparent);
  background: var(--surface);
  color: var(--onSurface);
  border-radius: 999px;
  padding: 0.55rem 0.95rem;
  font-size: 0.84rem;
  font-weight: 600;
  cursor: pointer;

  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
`;

const QuantityButton = styled(ActionButton)`
  min-width: 2rem;
  padding: 0.35rem 0.55rem;
`;

const PrimaryActionButton = styled(ActionButton)`
  background: var(--primary);
  border-color: var(--primary);
  color: var(--onPrimary, #fff);
`;

const FieldGrid = styled.div<{ $columns?: number }>`
  display: grid;
  gap: 0.45rem;
  grid-template-columns: repeat(${({ $columns = 1 }) => $columns}, minmax(0, 1fr));

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const ContactFieldGrid = styled(FieldGrid)`
  grid-template-columns: repeat(3, minmax(0, 1fr));

  @media (max-width: 760px) {
    grid-template-columns: 1fr;
  }
`;

const RegistrationFieldGrid = styled(FieldGrid)`
  grid-template-columns: repeat(2, minmax(0, 1fr));

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const CheckboxRow = styled.label`
  display: flex;
  align-items: center;
  gap: 0.55rem;
  font-size: 0.9rem;
  color: var(--onSurface);
`;

const MultiSelectWrap = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.45rem;
`;

const MultiSelectOption = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  font-size: 0.84rem;
  color: var(--onSurface);
`;

const getCheckedInAttendeeIndexes = (
  registrationId: string,
  tickets: RegistrantEditTicketRow[],
): number[] => {
  const rows = tickets
    .filter((ticket) => ticket.registration_id === registrationId)
    .sort((left, right) => {
      const leftNum = Number.parseInt(String(left.ticket_number || ''), 10);
      const rightNum = Number.parseInt(String(right.ticket_number || ''), 10);
      return (Number.isFinite(leftNum) ? leftNum : 0) - (Number.isFinite(rightNum) ? rightNum : 0);
    });

  const indexes: number[] = [];
  rows.forEach((ticket, fallbackIndex) => {
    const status = String(ticket.status || '').toLowerCase();
    if (status !== 'checked_in') return;
    const ticketNumber = Number.parseInt(String(ticket.ticket_number || ''), 10);
    indexes.push(Number.isFinite(ticketNumber) && ticketNumber > 0 ? ticketNumber - 1 : fallbackIndex);
  });
  return indexes;
};

const renderCustomFieldInput = (args: {
  field: EventOrderFormField;
  value: CustomFieldInputValue;
  onChange: (value: CustomFieldInputValue) => void;
  disabled?: boolean;
}) => {
  const { field, value, onChange, disabled } = args;
  const options = parseFieldOptions(field.options);

  if (field.field_type === 'long_text') {
    return (
      <Textarea
        value={String(value || '')}
        onChange={(event) => onChange(event.target.value)}
        placeholder={field.placeholder || ''}
        disabled={disabled}
      />
    );
  }

  if (field.field_type === 'select') {
    return (
      <Select value={String(value || '')} onChange={(event) => onChange(event.target.value)} disabled={disabled}>
        <option value="">{field.placeholder || 'Select one'}</option>
        {options.map((option) => (
          <option key={option} value={option}>
            {option}
          </option>
        ))}
      </Select>
    );
  }

  if (field.field_type === 'multi_select') {
    const selected = Array.isArray(value) ? value : [];
    return (
      <MultiSelectWrap>
        {options.map((option) => {
          const checked = selected.includes(option);
          return (
            <MultiSelectOption key={option}>
              <input
                type="checkbox"
                checked={checked}
                disabled={disabled}
                onChange={(event) => {
                  const next = event.target.checked
                    ? [...selected, option]
                    : selected.filter((item) => item !== option);
                  onChange(next);
                }}
              />
              {option}
            </MultiSelectOption>
          );
        })}
      </MultiSelectWrap>
    );
  }

  if (field.field_type === 'checkbox') {
    return (
      <CheckboxRow>
        <input
          type="checkbox"
          checked={Boolean(value)}
          disabled={disabled}
          onChange={(event) => onChange(event.target.checked)}
        />
        {field.label}
      </CheckboxRow>
    );
  }

  const inputType =
    field.field_type === 'email'
      ? 'email'
      : field.field_type === 'phone'
        ? 'tel'
        : field.field_type === 'number'
          ? 'number'
          : field.field_type === 'date'
            ? 'date'
            : 'text';

  return (
    <Input
      type={inputType}
      value={String(value || '')}
      onChange={(event) => onChange(event.target.value)}
      placeholder={field.placeholder || ''}
      disabled={disabled}
    />
  );
};

export const EventRegistrantEditModal: React.FC<EventRegistrantEditModalProps> = ({
  registration,
  formFields,
  registrationTickets,
  isSaving,
  onClose,
  onSave,
}) => {
  const initialState = React.useMemo(
    () => parseRegistrationEditState(registration, formFields),
    [registration, formFields],
  );
  const [state, setState] = React.useState<RegistrantEditState>(initialState);
  const [notice, setNotice] = React.useState<Notice | null>(null);

  React.useEffect(() => {
    setState(parseRegistrationEditState(registration, formFields));
    setNotice(null);
  }, [registration, formFields]);

  const quantity = Math.max(1, Math.min(MAX_REGISTRANT_EDIT_QUANTITY, Math.floor(Number(state.quantity || 1))));
  const checkedInAttendeeIndexes = React.useMemo(
    () => getCheckedInAttendeeIndexes(registration.id, registrationTickets),
    [registration.id, registrationTickets],
  );
  const orderLevelFields = React.useMemo(
    () => resolveOrderLevelEditableFields(formFields, registration.form_response),
    [formFields, registration.form_response],
  );
  const perAttendeeFields = React.useMemo(
    () => resolvePerAttendeeEditableFields(formFields, state.attendeeResponses),
    [formFields, state.attendeeResponses],
  );
  const previousQuantity = initialState.quantity;

  const buildEmptyAttendeeResponse = (attendeeIndex: number) => ({
    attendee_index: attendeeIndex,
    attendee_name: `Attendee ${attendeeIndex + 1}`,
    responses: Object.fromEntries(
      perAttendeeFields.map((field) => [
        field.field_key,
        field.field_type === 'checkbox' ? false : field.field_type === 'multi_select' ? [] : '',
      ]),
    ),
  });

  const changeQuantity = (nextQuantityRaw: number) => {
    const nextQuantity = Math.max(1, Math.min(MAX_REGISTRANT_EDIT_QUANTITY, Math.floor(nextQuantityRaw)));
    if (nextQuantity < quantity) {
      for (const checkedInIndex of checkedInAttendeeIndexes) {
        if (checkedInIndex >= nextQuantity) {
          setNotice({
            kind: 'error',
            message: `Cannot remove attendee #${checkedInIndex + 1} because their ticket is already checked in.`,
          });
          return;
        }
      }
    }

    setState((current) => {
      let attendees = [...current.attendees];
      let attendeeResponses = [...current.attendeeResponses];
      while (attendees.length < nextQuantity) {
        const nextIndex = attendees.length;
        attendees.push({ full_name: '', email: '', phone: '' });
        attendeeResponses.push(buildEmptyAttendeeResponse(nextIndex));
      }
      if (attendees.length > nextQuantity) {
        attendees = attendees.slice(0, nextQuantity);
        attendeeResponses = attendees.map((attendee, attendeeIndex) => {
          const existing = current.attendeeResponses[attendeeIndex];
          return {
            attendee_index: attendeeIndex,
            attendee_name: attendee.full_name || existing?.attendee_name || `Attendee ${attendeeIndex + 1}`,
            responses: existing?.responses || {},
          };
        });
      }
      return {
        ...current,
        quantity: nextQuantity,
        attendees,
        attendeeResponses,
      };
    });
    setNotice(null);
  };

  const updateAttendee = (index: number, patch: Partial<RegistrantEditAttendee>) => {
    setState((current) => {
      const attendees = current.attendees.map((attendee, attendeeIndex) =>
        attendeeIndex === index ? { ...attendee, ...patch } : attendee,
      );
      const nextPrimary = attendees[0] || { full_name: '', email: '', phone: '' };
      const attendeeResponses = current.attendeeResponses.map((entry, attendeeIndex) =>
        attendeeIndex === index
          ? { ...entry, attendee_name: patch.full_name ?? entry.attendee_name }
          : entry,
      );
      return {
        ...current,
        attendees,
        attendeeResponses,
        fullName: index === 0 ? patch.full_name ?? current.fullName : nextPrimary.full_name,
        email: index === 0 ? patch.email ?? current.email : current.email,
        phone: index === 0 ? patch.phone ?? current.phone : current.phone,
      };
    });
  };

  const updateAttendeeFieldValue = (attendeeIndex: number, fieldKey: string, value: CustomFieldInputValue) => {
    setState((current) => ({
      ...current,
      attendeeResponses: current.attendeeResponses.map((entry) =>
        entry.attendee_index === attendeeIndex
          ? {
              ...entry,
              responses: {
                ...entry.responses,
                [fieldKey]: value,
              },
            }
          : entry,
      ),
    }));
  };

  const addAttendee = () => {
    changeQuantity(quantity + 1);
  };

  const removeAttendee = (index: number) => {
    if (index === 0) return;
    if (isAttendeeSlotCheckedIn(checkedInAttendeeIndexes, index)) {
      setNotice({
        kind: 'error',
        message: `Cannot remove attendee #${index + 1} because their ticket is already checked in.`,
      });
      return;
    }
    setState((current) => {
      const attendees = current.attendees.filter((_, attendeeIndex) => attendeeIndex !== index);
      const attendeeResponses = attendees.map((attendee, attendeeIndex) => {
        const sourceIndex = attendeeIndex < index ? attendeeIndex : attendeeIndex + 1;
        const existing = current.attendeeResponses[sourceIndex];
        return {
          attendee_index: attendeeIndex,
          attendee_name: attendee.full_name || existing?.attendee_name || `Attendee ${attendeeIndex + 1}`,
          responses: existing?.responses || {},
        };
      });
      return {
        ...current,
        quantity: attendees.length,
        attendees,
        attendeeResponses,
      };
    });
    setNotice(null);
  };

  const handleSave = async () => {
    const validationError = validateRegistrantEditState({
      state,
      formFields,
      checkedInAttendeeIndexes,
      previousQuantity,
    });
    if (validationError) {
      setNotice({ kind: 'error', message: validationError });
      return;
    }

    const formResponse = buildFormResponsePayload({
      state,
      existingFormResponse: registration.form_response,
      formFields,
    });

    setNotice(null);
    try {
      await onSave({
        registrationId: registration.id,
        fullName: composeFullNameFromEditState(state),
        email: state.email.trim(),
        phone: state.phone.trim(),
        notes: state.notes.trim(),
        quantity,
        formResponse,
      });
    } catch (error) {
      setNotice({
        kind: 'error',
        message: error instanceof Error ? error.message : 'Could not update registrant.',
      });
    }
  };

  return (
    <Overlay
      onClick={() => {
        if (isSaving) return;
        onClose();
      }}
    >
      <Modal onClick={(event) => event.stopPropagation()}>
        <ModalAccent />
        <ModalHeader>
          <ModalTitle>Edit Registrant</ModalTitle>
          <CloseButton type="button" disabled={isSaving} onClick={onClose}>
            Close
          </CloseButton>
        </ModalHeader>
        <ModalScrollBody>
          {notice ? <Banner $kind={notice.kind}>{notice.message}</Banner> : null}

          <Section>
            <SectionTitle>Primary Contact</SectionTitle>
            <SectionPanel>
              <ContactFieldGrid>
                <Field>
                  <FieldLabel>Name</FieldLabel>
                  <Input
                  value={state.fullName}
                  onChange={(event) => {
                    const fullName = event.target.value;
                    setState((current) => ({
                      ...current,
                      fullName,
                      attendees: current.attendees.map((attendee, index) =>
                        index === 0 ? { ...attendee, full_name: fullName } : attendee,
                      ),
                    }));
                  }}
                />
              </Field>
              <Field>
                <FieldLabel>Email</FieldLabel>
                <Input
                  type="email"
                  value={state.email}
                  onChange={(event) => {
                    const email = event.target.value;
                    setState((current) => ({
                      ...current,
                      email,
                      attendees: current.attendees.map((attendee, index) =>
                        index === 0 ? { ...attendee, email } : attendee,
                      ),
                    }));
                  }}
                />
              </Field>
              <Field>
                <FieldLabel>Phone</FieldLabel>
                <Input
                  type="tel"
                  value={state.phone}
                  onChange={(event) => {
                    const phone = event.target.value;
                    setState((current) => ({
                      ...current,
                      phone,
                      attendees: current.attendees.map((attendee, index) =>
                        index === 0 ? { ...attendee, phone } : attendee,
                      ),
                    }));
                  }}
                />
                </Field>
              </ContactFieldGrid>
            </SectionPanel>
          </Section>

          <Section>
            <SectionTitle>Notes</SectionTitle>
            <SectionPanel>
              <Textarea
              value={state.notes}
              onChange={(event) => setState((current) => ({ ...current, notes: event.target.value }))}
              placeholder="Registration notes"
            />
            </SectionPanel>
          </Section>

          {orderLevelFields.length > 0 ? (
            <Section>
              <SectionTitle>Registration Fields</SectionTitle>
              <SectionPanel>
                <RegistrationFieldGrid>
                {orderLevelFields.map((field) => (
                  <Field key={field.field_key}>
                    <FieldLabel>
                      {field.label}
                      {field.is_required ? ' *' : ''}
                    </FieldLabel>
                    {renderCustomFieldInput({
                      field,
                      value: (state.orderLevelValues[field.field_key] as CustomFieldInputValue) ?? '',
                      onChange: (value) =>
                        setState((current) => {
                          const orderLevelValues = {
                            ...current.orderLevelValues,
                            [field.field_key]: value,
                          };
                          if (field.field_key !== 'first_name' && field.field_key !== 'last_name') {
                            return { ...current, orderLevelValues };
                          }
                          const fullName = composeFullNameFromEditState({ ...current, orderLevelValues });
                          return {
                            ...current,
                            orderLevelValues,
                            fullName,
                            attendees: current.attendees.map((attendee, index) =>
                              index === 0 ? { ...attendee, full_name: fullName } : attendee,
                            ),
                          };
                        }),
                    })}
                    {field.help_text ? <HelpText>{field.help_text}</HelpText> : null}
                  </Field>
                ))}
                </RegistrationFieldGrid>
              </SectionPanel>
            </Section>
          ) : null}

          <Section>
            <SectionTitle>Attendees</SectionTitle>
            <QuantityRow>
              <SectionHint>Quantity controls how many tickets this registration includes.</SectionHint>
              <QuantityControls>
                <QuantityButton
                  type="button"
                  disabled={isSaving || quantity <= 1}
                  onClick={() => changeQuantity(quantity - 1)}
                  aria-label="Decrease quantity"
                >
                  -
                </QuantityButton>
                <QuantityValue>{quantity}</QuantityValue>
                <QuantityButton
                  type="button"
                  disabled={isSaving || quantity >= MAX_REGISTRANT_EDIT_QUANTITY}
                  onClick={() => changeQuantity(quantity + 1)}
                  aria-label="Increase quantity"
                >
                  +
                </QuantityButton>
              </QuantityControls>
            </QuantityRow>
            <AttendeeStack>
            {state.attendees.map((attendee, index) => {
              const checkedIn = isAttendeeSlotCheckedIn(checkedInAttendeeIndexes, index);
              const responseEntry =
                state.attendeeResponses.find((entry) => entry.attendee_index === index) ||
                state.attendeeResponses[index];
              return (
                <AttendeeCard key={`attendee-${index}`}>
                  <AttendeeHeader>
                    <AttendeeTitle>{index === 0 ? 'Primary attendee' : `Attendee ${index + 1}`}</AttendeeTitle>
                    {checkedIn ? <CheckedInBadge>Checked in</CheckedInBadge> : null}
                  </AttendeeHeader>
                  <FieldGrid>
                    <Field>
                      <FieldLabel>Name</FieldLabel>
                      <Input
                        value={attendee.full_name}
                        onChange={(event) => updateAttendee(index, { full_name: event.target.value })}
                      />
                    </Field>
                    <Field>
                      <FieldLabel>Email</FieldLabel>
                      <Input
                        type="email"
                        value={attendee.email}
                        onChange={(event) => updateAttendee(index, { email: event.target.value })}
                      />
                    </Field>
                    <Field>
                      <FieldLabel>Phone</FieldLabel>
                      <Input
                        type="tel"
                        value={attendee.phone}
                        onChange={(event) => updateAttendee(index, { phone: event.target.value })}
                      />
                    </Field>
                  </FieldGrid>
                  {perAttendeeFields.length > 0 ? (
                    <FieldGrid>
                      {perAttendeeFields.map((field) => (
                        <Field key={`${index}-${field.field_key}`}>
                          <FieldLabel>
                            {field.label}
                            {field.is_required ? ' *' : ''}
                          </FieldLabel>
                          {renderCustomFieldInput({
                            field,
                            value: (responseEntry?.responses?.[field.field_key] as CustomFieldInputValue) ?? '',
                            onChange: (value) => updateAttendeeFieldValue(index, field.field_key, value),
                          })}
                          {field.help_text ? <HelpText>{field.help_text}</HelpText> : null}
                        </Field>
                      ))}
                    </FieldGrid>
                  ) : null}
                  {index > 0 ? (
                    <ActionButton
                      type="button"
                      disabled={isSaving || checkedIn}
                      onClick={() => removeAttendee(index)}
                    >
                      Remove attendee
                    </ActionButton>
                  ) : null}
                </AttendeeCard>
              );
            })}
            <ActionButton type="button" disabled={isSaving || quantity >= MAX_REGISTRANT_EDIT_QUANTITY} onClick={addAttendee}>
              Add attendee
            </ActionButton>
            </AttendeeStack>
          </Section>

          <ToolbarActionRow>
            <PrimaryActionButton type="button" disabled={isSaving} onClick={() => void handleSave()}>
              {isSaving ? 'Saving...' : 'Save changes'}
            </PrimaryActionButton>
            <ActionButton type="button" disabled={isSaving} onClick={onClose}>
              Cancel
            </ActionButton>
          </ToolbarActionRow>
        </ModalScrollBody>
      </Modal>
    </Overlay>
  );
};
